/**
 * Português (pt-BR) — dicionário canônico.
 *
 * Esta é a fonte da verdade. Todos os outros idiomas precisam implementar
 * EXATAMENTE as mesmas chaves. O tipo `Translations` em `src/i18n/index.ts`
 * é derivado deste arquivo, então o build do TypeScript quebra se `en.ts` ou
 * `es.ts` divergirem.
 *
 * As strings marcadas com `[legado]` reproduzem literalmente o texto exibido
 * pela versão Laravel em Blade/jQuery — não altere sem decisão de produto, elas
 * são a referência de paridade da conversão.
 */

const pt = {
  meta: {
    htmlLang: 'pt-BR',
  },
  common: {
    loading: 'Carregando...',
    close: 'Fechar',
    cancel: 'Cancelar',
    ok: 'OK',
    continue: 'Continuar',
    back: 'Voltar',
    error: 'Erro',
    attention: 'Atenção',
    success: 'Sucesso',
    oops: 'Oops...',
    copied: 'Copiado!',
  },
  layout: {
    loading: 'Carregando...',
    errorTitle: 'Erro ao carregar configurações',
    errorBody: 'Não foi possível carregar as configurações desta página. Tente novamente em instantes.',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Português',
    en: 'English',
    es: 'Español',
  },
  /** Home — passo 1: informar a linha. */
  home: {
    title: 'Recarga Fácil',
    subtitle: 'Informe o número que deseja recarregar',
    phoneLabel: 'Número do Celular',
    phonePlaceholder: '(00) 00000-0000',
    submit: 'Confirmar Linha',
    errors: {
      phoneRequired: 'Por favor, preencha seu telefone',
      phoneLength: 'O telefone deve ter o DDD + 9 dígitos',
      lineNotFound: 'Linha não encontrada.',
      lineCheckFailed: 'Erro ao validar a linha. Tente novamente.',
    },
  },
  /** Home — passo 2: validação do código SMS. */
  otp: {
    title: 'Segurança',
    /** {{phone}} é substituído pelo número formatado. */
    subtitle: 'Enviamos um código SMS para {{phone}}',
    codeLabel: 'Código SMS',
    codePlaceholder: '000000',
    submit: 'Validar Acesso',
    /** {{seconds}} é o tempo restante do cooldown de reenvio. */
    resendCountdown: 'Reenviar código ({{seconds}}s)',
    resend: 'Reenviar código',
    resent: 'Código reenviado com sucesso.',
    errors: {
      length: 'O código deve ter 6 dígitos',
      invalid: 'Código inválido ou expirado.',
      validateFailed: 'Erro ao validar código',
      resendFailed: 'Falha ao reenviar SMS.',
      sessionExpired: 'Sessão expirada. Inicie novamente.',
      cooldown: 'Aguarde um momento antes de solicitar um novo código para este número.',
    },
  },
  /** Lista de planos e seleção de forma de pagamento. */
  plans: {
    pageTitle: 'Escolha seu Plano',
    heading: 'Escolha o plano ideal',
    changeNumber: 'Alterar número',
    customerFallback: 'Cliente',
    planLabel: 'Plano',
    networkBadge: '4G/5G',
    currency: 'R$',
    perMonth: '/mês',
    select: 'Selecionar Plano',
  },
  /** Modal "Como você deseja pagar?" */
  payment: {
    chooseTitle: 'Como você deseja pagar?',
    pix: {
      name: 'PIX',
      hint: 'Aprovação imediata',
      badge: 'Ganhe tempo',
    },
    card: {
      name: 'Cartão de Crédito',
      hint: 'Pague agora, direto no site',
    },
    boleto: {
      name: 'Boleto Bancário',
      hint: 'Pode levar até 3 dias',
    },
    processingTitle: 'Processando...',
    processingBody: 'Aguarde enquanto geramos seu pagamento',
    errors: {
      generic: 'Erro ao processar.',
      timeout: 'A solicitação demorou muito. Verifique sua conexão.',
      noPix: 'Dados do PIX não retornados. Tente novamente.',
      noBoleto: 'Dados do boleto não retornados pela API.',
    },
  },
  /** Modal do PIX + polling de confirmação. */
  pix: {
    title: 'Pagamento via PIX',
    instructions: 'Escaneie o QR Code ou use o copia e cola.',
    copyCode: 'Copiar Código Pix',
    qrAlt: 'QR Code do PIX',
    confirmedTitle: 'Pagamento Confirmado!',
    confirmedBody: 'Sua recarga foi realizada com sucesso.',
    timeoutTitle: 'Tempo esgotado',
    timeoutBody:
      'Não recebemos a confirmação do pagamento. Se você já fez o PIX, aguarde alguns minutos. Caso contrário, gere um novo código.',
    waiting: 'Aguardando a confirmação do pagamento...',
  },
  /** Modal do boleto. */
  boleto: {
    title: 'Boleto Bancário',
    instructions: 'Utilize o código de barras ou abra o link.',
    copyBarcode: 'Copiar Código de Barras',
    open: 'Abrir Boleto (PDF)',
  },
  /** Tela de pagamento com cartão de crédito. */
  creditCard: {
    pageTitle: 'Cartão de Crédito',
    summaryTitle: 'Resumo do Pedido',
    summarySubtitle: 'Confira os dados da sua recarga.',
    selectedPlan: 'Plano Selecionado',
    amount: 'Valor',
    line: 'Linha',
    document: 'Documento',
    formTitle: 'Dados do Pagamento',
    nameLabel: 'Nome no Cartão',
    namePlaceholder: 'Como está impresso no cartão',
    numberLabel: 'Número do Cartão',
    numberPlaceholder: '0000 0000 0000 0000',
    expiryLabel: 'Validade',
    expiryPlaceholder: 'MM/AAAA',
    cvvLabel: 'CVV',
    cvvPlaceholder: '123',
    /** Rótulo acessível do ícone/bandeira exibido dentro do campo do número. */
    brandIconAlt: 'Bandeira do cartão',
    recurrenceLabel: 'Ativar recorrência no cartão',
    recurrenceHint: 'O pagamento será renovado automaticamente todo mês.',
    submit: 'Efetuar Pagamento',
    submitting: 'Processando...',
    secureFooter: 'Ambiente 100% Seguro. Seus dados estão protegidos.',
    approvedTitle: 'Pagamento Aprovado!',
    approvedBody: 'Sua recarga foi realizada com sucesso.',
    backHome: 'Voltar para o início',
    errors: {
      generic: 'Erro ao processar pagamento.',
      failed: 'Falha no processamento',
      invalidName: 'Informe o nome como está impresso no cartão.',
      invalidNumber: 'Número do cartão inválido.',
      invalidExpiry: 'Validade inválida. Use o formato MM/AAAA.',
      expiredCard: 'Cartão vencido.',
      invalidCvv: 'CVV inválido.',
      recaptchaFailed: 'Não foi possível concluir a verificação de segurança. Recarregue a página e tente novamente.',
      recaptchaUnavailable: 'O pagamento com cartão está temporariamente indisponível. Use PIX ou boleto.',
    },
  },
  /**
   * Fluxo A (legado) — página `/renova_plano?t={tipo}&id={id}`, consumida pelo
   * produto Link de Venda Direta. Os textos são idênticos aos da view Blade.
   */
  legacyPayment: {
    /** {{name}} é o primeiro nome do cliente. */
    greeting: 'Olá, {{name}}',
    /** {{product}} vem de `product.*` abaixo. */
    subtitle: 'Faça o pagamento da sua {{product}}',
    product: {
      recarga: 'recarga',
      ativacao: 'ativação',
      compra: 'compra',
    },
    pixDelay: 'O pagamento pode levar até 15 minutos para ser compensado.',
    pixCopyIntroHighlight: 'Copie e cole',
    pixCopyIntro: 'o código abaixo no app do seu banco, dentro da opção de PIX.',
    pixCopyButton: 'Copiar código',
    pixScanHighlight: 'Escaneie o QRCode',
    pixScanIntro: 'abaixo após entrar em Pix, no app do seu banco',
    pixQrAlt: 'Seu QR code',
    boletoDelay: 'O pagamento pode levar até 2 dias úteis para ser compensado.',
    boletoView: 'VISUALIZAR BOLETO',
    boletoCopy: 'COPIAR CÓDIGO',
    linkIntro: 'Escolha se deseja pagar com pix, cartão de crédito ou boleto.',
    linkButton: 'ESCOLHER FORMA DE PAGAMENTO',
    supportPrefix: 'Dúvidas? Chame nosso',
    supportLink: 'suporte',
    rightsReserved: 'Todos os direitos reservados',
    notFound: 'Falha ao localizar o pagamento.',
  },
  /** Rodapé compartilhado. */
  footer: {
    customerService: 'Central de Atendimento',
    whatsapp: 'WhatsApp',
    androidApp: 'Baixe o App no Android',
    googlePlay: 'Google Play',
    iosApp: 'Baixe o App no iPhone',
    appStore: 'App Store',
    website: 'Acesse nosso site',
    /** {{year}} é o ano corrente. */
    copyright:
      '© {{year}} Nogle Tech. Todos os direitos reservados. Proibida a reprodução sem autorização. Este é um ambiente seguro. Todos os dados são criptografados para proteger sua privacidade.',
    nogleAlt: 'Marca Nogle',
  },
  /** Página de marca inativa / rota inexistente (equivalente ao service_unavailable). */
  notFound: {
    pageTitle: 'Página não encontrada',
    code: '404',
    title: 'Página não encontrada',
    body: 'Desculpe, não conseguimos encontrar a página que você está procurando.',
    /** {{year}} é o ano corrente. */
    copyright: '© {{year}} Recarga Fácil. Todos os direitos reservados.',
  },
  /** Mensagens de erro da camada de API. */
  api: {
    sessionExpired: 'Sua sessão expirou. Informe o número novamente.',
  },
};

export default pt;
